import { CredentialProposalStatus } from "@api/shared-runtime/enums";
import {
  MAX_CREDENTIAL_IMPORT_BATCH_SIZE,
  MAX_CREDENTIAL_IMPORT_DOCUMENTS,
} from "@api/shared-runtime/credential-import-limits";
import { hasProviderImportChanges } from "./provider-import-field-selection";
import {
  ProviderOfferAction,
  ProviderOfferFilter,
  type ProviderImportItem,
  type ProviderOffer,
  type ProviderOfferSelection,
} from "../components/provider-imports/provider-import-types";

export function getProviderOfferLabel(offer: ProviderOffer): string {
  return [offer.label, offer.identityLabel].filter(Boolean).join(" · ");
}

export function filterProviderOffers({
  offers,
  filter,
}: {
  offers: ProviderOfferSelection[];
  filter: ProviderOfferFilter;
}): ProviderOfferSelection[] {
  if (filter === ProviderOfferFilter.All) {
    return offers;
  }
  if (filter === ProviderOfferFilter.Dismissed) {
    return offers.filter(
      (offer) =>
        !!offer.dismissedAt &&
        offer.status === CredentialProposalStatus.Pending,
    );
  }
  return offers.filter(
    (offer) =>
      offer.isActionable &&
      offer.reviewRequired &&
      !offer.dismissedAt &&
      offer.status === CredentialProposalStatus.Pending,
  );
}

export function canApplyProviderOffer(
  offer: ProviderOfferSelection,
  action: ProviderOfferAction,
): boolean {
  if (action === ProviderOfferAction.Import) {
    return hasProviderImportChanges(offer);
  }
  return (
    offer.isActionable &&
    offer.reviewRequired &&
    offer.status === CredentialProposalStatus.Pending &&
    !offer.dismissedAt
  );
}

export function providerImportItem(
  offer: ProviderOfferSelection,
): ProviderImportItem {
  const held = new Set(offer.targetDocuments.map((document) => document.id));
  const selectedDocumentIds = offer.sourceDocuments
    .filter((document) => !held.has(document.id))
    .map((document) => document.id);
  // A one-click import includes all new evidence. Never silently drop documents
  // to fit the API limit when there is no document-selection step.
  if (selectedDocumentIds.length > MAX_CREDENTIAL_IMPORT_DOCUMENTS) {
    throw new Error(
      `${getProviderOfferLabel(offer)} has ${selectedDocumentIds.length} new documents; the import limit is ${MAX_CREDENTIAL_IMPORT_DOCUMENTS}.`,
    );
  }
  return {
    offerId: offer.id,
    expectedVersion: offer.expectedVersion,
    selectedDocumentIds,
    ...(offer.selectedFieldPaths === undefined
      ? {}
      : { selectedFieldPaths: [...offer.selectedFieldPaths] }),
  };
}

export class ProviderOfferActionError extends Error {
  readonly completed: number;
  constructor({
    cause,
    action,
    offers,
    completed,
  }: {
    cause: unknown;
    action: ProviderOfferAction;
    offers: ProviderOfferSelection[];
    completed: number;
  }) {
    let reason =
      cause instanceof Error
        ? cause.message
        : "The save could not be confirmed.";
    for (const offer of offers) {
      reason = reason.replaceAll(offer.id, getProviderOfferLabel(offer));
    }
    const verb =
      action === ProviderOfferAction.Import ? "Imports" : "Dismissals";
    super(
      `${verb} confirmed: ${completed} of ${offers.length}. ${reason} Refresh before trying again.`,
    );
    this.completed = completed;
  }
}

export async function applyProviderOffers({
  action,
  offers,
  transport,
  onProgress,
}: {
  action: ProviderOfferAction;
  offers: ProviderOfferSelection[];
  transport: {
    importOne: (item: ProviderImportItem) => Promise<unknown>;
    importBatch: (items: ProviderImportItem[]) => Promise<unknown>;
    dismissOne: (item: {
      offerId: string;
      expectedVersion: string;
    }) => Promise<unknown>;
    refresh: () => Promise<unknown>;
  };
  onProgress: (completed: number) => void;
}): Promise<number> {
  let completed = 0;
  let failure: unknown;
  try {
    if (action === ProviderOfferAction.Import) {
      // Validate every record before starting, then retain the confirmed list's
      // version tokens across chunks. A changed later chunk stops the operation.
      const items = offers.map(providerImportItem);
      if (items.length === 1) {
        await transport.importOne(items[0]);
        completed = 1;
        onProgress(completed);
      } else {
        for (
          let offset = 0;
          offset < items.length;
          offset += MAX_CREDENTIAL_IMPORT_BATCH_SIZE
        ) {
          const batch = items.slice(
            offset,
            offset + MAX_CREDENTIAL_IMPORT_BATCH_SIZE,
          );
          await transport.importBatch(batch);
          completed += batch.length;
          onProgress(completed);
        }
      }
    } else {
      for (const offer of offers) {
        await transport.dismissOne({
          offerId: offer.id,
          expectedVersion: offer.expectedVersion,
        });
        completed += 1;
        onProgress(completed);
      }
    }
  } catch (cause) {
    failure = cause;
  }
  // Refresh once even after partial success, so completed records disappear
  // from Available and a new confirmation only includes the remaining work.
  try {
    await transport.refresh();
  } catch (cause) {
    failure ??= cause;
  }
  if (failure) {
    throw new ProviderOfferActionError({
      cause: failure,
      action,
      offers,
      completed,
    });
  }
  return completed;
}
